import { api } from '@/api';
import {
  AvailableUpdatePackageInfoTypeEnum,
  AvailableUpdateUpgradeResponseDto,
  AvailableUpdateUpgradeResponseDtoTypeEnum,
  instanceOfUpdateUpgradeStatusDto,
  RunUpdateUpgradeRequestDto,
  UpdateUpgradeStatusDto,
} from '@/api/generator';
import { NotificationMessage } from '@/components/notifications/types';
import { stopErrorPropagation } from '@/errorHandling';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import { useExceptionDetails } from '@/errorHandling/useExceptionDetails';
import { useStoreErrorNotification } from '@/errorHandling/useStoreErrorNotification';
import { useSystemStore } from '@/stores/systemStore';
import { UpdateType } from '@/views/UpdateView/components/UpdateWizard.vue';
import { I18nKey } from '@/utils/typings';
import { TFunction } from 'i18next';
import { computed, readonly, ref } from 'vue';
import { translateI18nInfo } from '@/errorHandling/errorUtils';

type UpdateState = 'idle' | 'success' | 'failed';

const state = ref<UpdateState>('idle');
const checkForUpdatesErrorNotification = ref<NotificationMessage | undefined>(undefined);
const available = ref<AvailableUpdateUpgradeResponseDto | undefined>(undefined);
const updateType = ref<UpdateType | undefined>(undefined);
const notification = ref<NotificationMessage | undefined>(undefined);
const csoPortalInstallationUri = ref<string | null>();
const exceptionNotification = ref<NotificationMessage | undefined>(undefined);

export function useAppUpdate(
  t: TFunction,
  backendErrorHandler: (error: AnyApiError) => AnyApiError | undefined,
) {
  const systemStore = useSystemStore();

  // Error handling setup
  const {
    errorHandler: storeErrorHandler,
    clearError,
    notificationMessage,
  } = useStoreErrorNotification({
    showNotificationInToast: true,
    notificationMessage: notification,
    closable: false,
  });

  const {
    handleExceptionDetails,
    reset: resetExceptionDetails,
    loggedInUsers,
  } = useExceptionDetails(t, exceptionNotification);

  const ptwEdgeUpdateStatusResponse = ref<UpdateUpgradeStatusDto>();
  const installedVersion = computed(() => {
    if (available.value?.installedVersion) {
      return available.value.installedVersion;
    } else {
      return systemStore.version;
    }
  });

  function reset() {
    clearError();
    notification.value = undefined;
    resetExceptionDetails();
  }

  async function setCsoPortalInstallationUri(uri: string | null = null) {
    csoPortalInstallationUri.value = uri;
  }

  async function checkForNewUpdates() {
    await systemStore.fetchSystemData();
    available.value = undefined;
    checkForUpdatesErrorNotification.value = undefined;
    const { success, result } = await api.ptwEdgeUpdateApi.checkForNewUpdates([
      (error) => {
        if (
          error.name === ApiError.serviceUnavailable ||
          error.name === ApiError.badGateway ||
          error.name === ApiError.unauthorized
        ) {
          const message = translateI18nInfo(error.error?.exception?.i18n);

          checkForUpdatesErrorNotification.value = {
            type: 'error',
            title: t('ErrorTitle'),
            message,
            closable: false,
          };
        }
        // The trailing `stopErrorPropagation` suppresses every error, matched or not.
        return error;
      },
      stopErrorPropagation,
    ]);
    if (success && result) {
      available.value = result;
    }
  }

  async function startUpdate(
    runUpdateUpgradeRequestDto: RunUpdateUpgradeRequestDto,
    type: UpdateType,
  ) {
    updateType.value = undefined;
    reset();

    const { result, success } = await api.ptwEdgeUpdateApi.runUpdateUpgrade(
      {
        runUpdateUpgradeRequestDto,
      },
      [handleExceptionDetails, backendErrorHandler, storeErrorHandler, stopErrorPropagation],
    );

    if (success && result) {
      state.value = 'success';
      updateType.value = type;
      await setCsoPortalInstallationUri(result.csoPortalInstallationUri);
    } else {
      state.value = 'failed';
    }

    return success;
  }

  async function syncPtwEdgeUpdateStatus(type: UpdateType) {
    updateType.value = type;
    const { result } = await api.ptwEdgeUpdateApi.syncUpdateUpgradeState([
      (error) => {
        const i18nKey =
          error.name === ApiError.internalServer ? error.error?.exception?.i18n?.key : undefined;

        if (i18nKey === 'Exceptions.PtwEdgeUpdate.NoUpdateIsRunningMsg') {
          state.value = 'idle';

          notification.value = {
            type: 'info',
            message: t(i18nKey as I18nKey) as unknown as string,
            closable: false,
          };
        }
        // The trailing `stopErrorPropagation` suppresses every error, matched or not.
        return error;
      },
      stopErrorPropagation,
    ]);
    if (result) {
      ptwEdgeUpdateStatusResponse.value = result;
      available.value = {
        $type:
          AvailableUpdateUpgradeResponseDtoTypeEnum.PtwPtw100AppZeroApplicationSharedCsoPortalDtoAvailableUpdateUpgradeResponseDtoPtwPtw100AppZeroApplicationShared,
        installedVersion: instanceOfUpdateUpgradeStatusDto(ptwEdgeUpdateStatusResponse.value)
          ? ptwEdgeUpdateStatusResponse.value.fromVersion
          : undefined,
        latestUpdateInfo:
          updateType.value === 'update' &&
          instanceOfUpdateUpgradeStatusDto(ptwEdgeUpdateStatusResponse.value)
            ? {
                $type:
                  AvailableUpdatePackageInfoTypeEnum.PtwPtw100AppZeroApplicationSharedCsoPortalDtoAvailableUpdatePackageInfoPtwPtw100AppZeroApplicationShared,
                version: ptwEdgeUpdateStatusResponse.value.targetVersion,
              }
            : undefined,
        latestUpgradeInfo:
          updateType.value === 'upgrade' &&
          instanceOfUpdateUpgradeStatusDto(ptwEdgeUpdateStatusResponse.value)
            ? {
                $type:
                  AvailableUpdatePackageInfoTypeEnum.PtwPtw100AppZeroApplicationSharedCsoPortalDtoAvailableUpdatePackageInfoPtwPtw100AppZeroApplicationShared,
                version: ptwEdgeUpdateStatusResponse.value.targetVersion,
              }
            : undefined,
      };

      if (ptwEdgeUpdateStatusResponse.value.exitCode === 0) {
        state.value = 'success';
        notification.value = {
          type: 'success',
          message:
            t('The_updateType_WasComletedSuccessfully', {
              updateType: updateType.value,
            }) ?? '',
          closable: false,
        };
      } else {
        state.value = 'failed';
        notification.value = {
          type: 'error',
          message: ptwEdgeUpdateStatusResponse.value.errorMessage ?? '',
          closable: false,
        };
      }
    }
  }

  function openProgress(type: UpdateType) {
    if (csoPortalInstallationUri.value === undefined) {
      throw new Error('Update was not started yet!');
    }
    updateType.value = type;

    // redirect to progress page, from which the user will be able to navigate back to the current page
    const redirectUri = `${location.origin}${location.pathname}?sync=true&updateType=${encodeURIComponent(updateType.value)}#tab:update`;
    const installationProgressUrl = `${csoPortalInstallationUri.value}&redirectUri=${encodeURIComponent(redirectUri)}`;

    location.replace(installationProgressUrl);
  }

  return {
    state: readonly(state),
    notification: readonly(notification),
    installedVersion,
    available: computed(() => ({
      update: available.value?.latestUpdateInfo,
      upgrade: available.value?.latestUpgradeInfo,
    })),
    ptwEdgeUpdateStatus: readonly(ptwEdgeUpdateStatusResponse),
    updateType: readonly(updateType),
    csoPortalInstallationUri: readonly(csoPortalInstallationUri),
    checkForUpdatesErrorNotification: readonly(checkForUpdatesErrorNotification),
    startUpdate,
    checkForNewUpdates,
    clearNotification: reset,
    syncPtwEdgeUpdateStatus,
    openProgress,
    // Expose exception handling
    exceptionNotification: readonly(exceptionNotification),
    notificationMessage: readonly(notificationMessage),
    loggedInUsers: readonly(loggedInUsers),
  };
}
